import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { photoService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PhotoLightbox from '../PhotoLightbox/PhotoLightbox';
import { FiFolder } from 'react-icons/fi';
import './ProfilePhotos.css';

const ProfilePhotos = ({ memberId, isOwnProfile }) => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, [memberId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [photosRes, albumsRes] = await Promise.all([
        isOwnProfile
          ? photoService.getMyPhotos()
          : photoService.getMemberPhotos(memberId),
        isOwnProfile
          ? photoService.getMyAlbums()
          : photoService.getMemberAlbums(memberId)
      ]);

      if (photosRes.success) {
        const photoData = Array.isArray(photosRes.data)
          ? photosRes.data
          : (photosRes.data.photos || photosRes.data.items || []);
        setPhotos(photoData);
      }

      if (albumsRes.success) {
        const albumData = Array.isArray(albumsRes.data)
          ? albumsRes.data
          : (albumsRes.data.albums || albumsRes.data.items || []);
        setAlbums(albumData);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (photo, index) => {
    setLightboxPhoto(photo);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setLightboxIndex(newIndex);
      setLightboxPhoto(photos[newIndex]);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await photoService.deletePhoto(photoId);
      if (response.success) {
        setPhotos(prev => prev.filter(p => p.photoID !== photoId));
        closeLightbox();
      }
    } catch (err) {
      console.error('Failed to delete photo:', err);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <span className="spinner"></span>
        <p>Loading photos...</p>
      </div>
    );
  }

  return (
    <div className="profile-photos-section">
      {/* Albums */}
      {albums.length > 0 && (
        <div className="profile-albums">
          <h4>Albums</h4>
          <div className="profile-albums-grid">
            {albums.map(album => (
              <Link
                key={album.albumID}
                to={`/photos/album/${album.albumID}`}
                state={{ from: 'profile', memberID: memberId }}
                className="profile-album-card"
              >
                <div className="profile-album-cover">
                  {album.coverPhotoURL ? (
                    <img src={album.coverPhotoURL} alt={album.albumName} />
                  ) : (
                    <div className="profile-album-placeholder">
                      <FiFolder />
                    </div>
                  )}
                </div>
                <div className="profile-album-info">
                  {album.eventID && (
                    <span className="album-event-label">Event</span>
                  )}
                  <span className="profile-album-name">{album.albumName}</span>
                  <span className="profile-album-count">{album.photoCount || 0} photos</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* All Photos */}
      <div className="profile-all-photos">
        <h4>All Photos</h4>
        {photos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📷</div>
            <h3 className="empty-state-title">No photos yet</h3>
            <p>{isOwnProfile ? 'Upload photos from the Photos page!' : 'No photos to show.'}</p>
          </div>
        ) : (
          <div className="profile-photos-grid">
            {photos.map((photo, index) => (
              <div
                key={photo.photoID}
                className="profile-photo-item"
                onClick={() => openLightbox(photo, index)}
              >
                {photo.fileType === 'video' ? (
                  <>
                    {photo.thumbnailURL ? (
                      <img src={photo.thumbnailURL} alt={photo.caption || 'Video'} />
                    ) : (
                      <div className="video-tile-placeholder" />
                    )}
                    <div className="video-play-overlay">▶</div>
                  </>
                ) : (
                  <img
                    src={photo.thumbnailURL || photo.fileURL}
                    alt={photo.caption || 'Photo'}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox(-1)}
          onNext={() => navigateLightbox(1)}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < photos.length - 1}
          onDelete={lightboxPhoto.memberID === user?.memberID || user?.isAdmin ? handleDeletePhoto : null}
          onCaptionUpdated={(photoId, newCaption) => {
            setPhotos(prev => prev.map(p => p.photoID === photoId ? { ...p, caption: newCaption } : p));
            setLightboxPhoto(prev => ({ ...prev, caption: newCaption }));
          }}
        />
      )}
    </div>
  );
};

export default ProfilePhotos;